class RadioQuantitySelector extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();

    // Number input inside your <quantity-input> (actual form quantity)
    this.qtyNumberInput = document.querySelector('.quantity__input');
    // All the radios
    this.radios = Array.from(this.querySelectorAll('.radio-quantity-option__input'));

    // Bind radio clicks
    this.radios.forEach((radio) => {
      radio.addEventListener('change', this.onRadioChange.bind(this), {
        signal: this.abortController.signal,
      });
    });

    // Subscribe to FoxTheme pub/sub instead
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
        this.unsubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange.bind(this)
        );
        
        // Listen for cart additions to reset to first radio
        this.cartSubscriber = FoxTheme.pubsub.subscribe(
        FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
        this.handleCartUpdate.bind(this)
        );
    }

    // Initial selection
    const checked = this.radios.find((r) => r.checked) || this.radios[0];
    if (checked) this.applyQtyFromRadio(checked);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    if (this.unsubscriber) this.unsubscriber();
    if (this.cartSubscriber) this.cartSubscriber();
  }

  onRadioChange(e) {
    this.applyQtyFromRadio(e.currentTarget);
  }

  handleVariantChange(event) {
        //console.log('Variant change from pubsub:', event);
        const variant = event.data?.variant;
        
        if (variant && variant.price) {
            //console.log('Updating prices from pubsub with:', variant.price);
            this.updatePrices(variant.price);
        }
        
        // Reset to first radio
        const first = this.radios[0];
        if (first) {
            first.checked = true;
            this.applyQtyFromRadio(first);
        }
    }

  handleCartUpdate(event) {
    //console.log('Cart updated, resetting to first radio option');
    // After cart update, reset to first radio option
    const first = this.radios[0];
    if (first) {
      first.checked = true;
      this.applyQtyFromRadio(first);
    }
  }

  updatePrices(variantPrice) {
    //console.log('updatePrices called with:', variantPrice); // Debug log
    
    const radioOptions = this.querySelectorAll('.radio-quantity-option');
    //console.log('Found radio options:', radioOptions.length); // Debug log
    
    radioOptions.forEach((option, index) => {
      const input = option.querySelector('.radio-quantity-option__input');
      const quantity = parseInt(input.dataset.qty);
      const discountPercent = parseFloat(input.dataset.discount || 0);
      
      //console.log(`Option ${index + 1}: qty=${quantity}, discount=${discountPercent}%`); // Debug log
      
      // Calculate new prices (variantPrice is already in cents)
      const calculatedPrice = variantPrice * quantity;
      const discountMultiplier = 1 - (discountPercent / 100);
      const finalPrice = calculatedPrice * discountMultiplier;
      
      //console.log(`Calculated: ${calculatedPrice}, Final: ${finalPrice}`); // Debug log
      
      // Get price elements
      const priceWrapper = option.querySelector('.radio-quantity-option__price-wrapper');
      if (!priceWrapper) {
        //console.log('No price wrapper found for option', index); // Debug log
        return;
      }
      
      const originalPriceEl = priceWrapper.querySelector('.radio-quantity-option__price--original');
      const discountedPriceEl = priceWrapper.querySelector('.radio-quantity-option__price--discounted');
      const regularPriceEl = priceWrapper.querySelector('.radio-quantity-option__price:not(.radio-quantity-option__price--original):not(.radio-quantity-option__price--discounted)');
      
      // Update prices based on whether discount exists
      if (discountPercent > 0 && originalPriceEl && discountedPriceEl) {
        //console.log('Updating discounted option'); // Debug log
        originalPriceEl.textContent = this.formatMoney(calculatedPrice);
        discountedPriceEl.textContent = this.formatMoney(finalPrice);
        input.dataset.price = finalPrice;
      } else if (regularPriceEl) {
        //console.log('Updating regular option'); // Debug log
        regularPriceEl.textContent = this.formatMoney(calculatedPrice);
        input.dataset.price = calculatedPrice;
      } else {
        //console.log('No price elements found'); // Debug log
      }
    });
  }

  formatMoney(cents) {
    // Use FoxTheme's formatMoney function with the current money format
        if (typeof FoxTheme !== 'undefined' && FoxTheme.Currency && FoxTheme.Currency.formatMoney) {
            try {
              const moneyFormat = FoxTheme.settings?.moneyFormat;
              return FoxTheme.Currency.formatMoney(cents, moneyFormat);
            } catch (error) {
            //console.warn('Error formatting money with FoxTheme:', error);
            }
        }
        
        // Fallback formatting
        const dollars = (cents / 100).toFixed(2);
        return `$${dollars}`;
    }

  applyQtyFromRadio(radio) {
    if (!this.qtyNumberInput) return;
    const raw = parseInt(radio.dataset.qty, 10);
    if (isNaN(raw)) return;

    const min = this.qtyNumberInput.min ? parseInt(this.qtyNumberInput.min, 10) : 1;
    const step = this.qtyNumberInput.step ? parseInt(this.qtyNumberInput.step, 10) : 1;
    const max = this.qtyNumberInput.max ? parseInt(this.qtyNumberInput.max, 10) : null;

    let v = Math.max(raw, min);
    v = min + Math.round((v - min) / step) * step;
    if (max !== null) v = Math.min(v, max);

    const prev = this.qtyNumberInput.value;
    this.qtyNumberInput.value = v;
    this.qtyNumberInput.dispatchEvent(new Event('input', { bubbles: true }));
    if (prev != v) this.qtyNumberInput.dispatchEvent(new Event('change', { bubbles: true }));
    
    // Update visual state
    this.updateVisualState(radio);
  }

  updateVisualState(selectedRadio) {
    // Remove is-checked from all labels
    this.radios.forEach((r) => {
      const label = r.nextElementSibling;
      if (label) label.classList.remove('is-checked');
    });
    
    // Add is-checked to selected label
    const selectedLabel = selectedRadio.nextElementSibling;
    if (selectedLabel) selectedLabel.classList.add('is-checked');
  }
}
customElements.define('radio-quantity-selector', RadioQuantitySelector);